/**
 * FirstStrategyNudge — the "what's next" hand-off shown when a brand's universe
 * reaches maturity while the brand still has no strategy.
 *
 * Two pieces:
 *  - FirstStrategyNudgeModal: a one-time (per brand, localStorage) popup that
 *    lands the moment the universe crosses into "mature" and points at the
 *    strategy page.
 *  - StrategyReadyCard: a persistent rail strip that stays as long as the
 *    universe is mature and the brand has no strategy — so dismissing the
 *    popup never strands the user without the next step. Styled to match the
 *    universe's other rail strips.
 *
 * Both go to the strategy page's index, which is the start: one line there
 * and Creo creates the strategy.
 */
import { useNavigate } from "react-router";
import { KitModal } from "./kit/Modal";
import { KitButton } from "./kit/Button";

const seenKey = (brandId: string) => `co:strategy:firstNudgeSeen:${brandId}`;

export function hasSeenFirstStrategyNudge(brandId: string): boolean {
  try {
    return localStorage.getItem(seenKey(brandId)) === "1";
  } catch {
    return false;
  }
}

export function markFirstStrategyNudgeSeen(brandId: string) {
  try {
    localStorage.setItem(seenKey(brandId), "1");
  } catch {
    /* storage blocked — worst case the popup returns next visit */
  }
}

function useGoToFirstStrategy() {
  const navigate = useNavigate();
  return () => navigate("/dashboard/strategy");
}

/**
 * Universe / Strategy nudge: the kit's confirm dialog (Dialog / Confirm), the
 * title in sentence case, one line of why, and the one primary.
 */
export function FirstStrategyNudgeModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  /** Called on intentional close (X or CTA) — mark seen here. */
  onClose: () => void;
}) {
  const goToStrategy = useGoToFirstStrategy();
  return (
    <KitModal
      isOpen={isOpen}
      // Outside click + Esc must not dismiss — only the X / CTA.
      isDismissable={false}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Universe is mature"
      description="Ready for your first strategy."
    >
      <div className="mt-2 flex w-full justify-end">
        <KitButton
          variant="primary"
          onClick={() => {
            onClose();
            goToStrategy();
          }}
        >
          Create strategy
        </KitButton>
      </div>
    </KitModal>
  );
}

export function StrategyReadyCard() {
  const goToStrategy = useGoToFirstStrategy();
  return (
    <div className="co-glass-chrome flex w-full items-center gap-2.5 rounded-card py-2 pl-3.5 pr-2">
      <span
        aria-hidden
        className="size-[7px] flex-none rounded-full bg-co-live"
        style={{ boxShadow: "0 0 8px color-mix(in srgb, var(--co-live) 60%, transparent)" }}
      />
      <span className="min-w-0 flex-1 truncate text-[13px] text-co-text2">
        <b className="font-medium text-co-text">Universe mature:</b> ready for a strategy
      </span>
      <KitButton variant="primary" size="sm" className="flex-none" onClick={goToStrategy}>
        Start strategy
      </KitButton>
    </div>
  );
}
